import {
  ContentModule,
  MotionOptions,
  ModuleProperties,
  useMotionOptions,
  applyTween,
  useShowInstructions,
  WithHtmlId,
} from '@backstage-components/base';
import {ReactNode, FC, VFC, useState} from 'react';
import {css, SerializedStyles} from '@emotion/react';
import {
  SchemaType,
  instructions,
  reactName as namespace,
} from './ButtonDefinition';
import {
  Button as ButtonElm,
  ButtonProps,
  keyframes,
  Link,
  LinkProps,
} from '@chakra-ui/react';
import {useSubscription} from 'observable-hooks';
import {motion} from 'framer-motion';
import {buttonLinkStyles, sharedBtnStyles} from './ButtonStyles';

interface MotionProps {
  /** The Framer Motion variants and inital variant options */
  motionOptions?: MotionOptions;
  /** The current variant animation state of the Framer Motion component */
  animate?: string;
}

interface SharedProps extends ModuleProperties {
  buttonColor?: string;
  textColor?: string;
  children?: ReactNode;
  /**
   * @default false
   */
  disabled?: boolean;
  /**
   * @default false
   */
  isLoading?: boolean;
  fontSize?: string;
  borderColor?: string;
  padding?: string;
  fontFamily?: string;
  borderRadius?: string;
  borderWidth?: string;
  borderStyle?: string;
  width?: string;
  motionProps?: MotionProps;
}

interface HtmlLinkProps {
  /**
   * @default false
   */
  isExternal?: boolean;
  href: string;
}

interface HtmlButtonProps {
  /**
   * @default 'button'
   */
  htmlType?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
}

export type IButtonProps = (HtmlButtonProps | HtmlLinkProps) &
  SharedProps & {cssProp?: SerializedStyles};

export type ButtonComponentDefinition = ContentModule<
  'Button',
  IButtonProps & SchemaType
>;

const spinAround = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg)
  }
`;

// framer motion components for Chakra UI
const MotionBtn = motion<ButtonProps>(ButtonElm);
const MotionLink = motion<LinkProps>(Link);

const Button: FC<IButtonProps & WithHtmlId> = (props) => {
  const spinAnimation = `${spinAround} 0.5s infinite linear`;
  const {
    children,
    disabled = false,
    isLoading = false,
    motionProps,
    id,
  } = props;

  return 'href' in props ? (
    <MotionLink
      id={id}
      href={props.href}
      isExternal={props.isExternal}
      aria-label={typeof children === 'string' ? children : 'Link'}
      {...sharedBtnStyles({...props, spinAnimation})}
      {...buttonLinkStyles}
      {...motionProps}
    >
      {children}
    </MotionLink>
  ) : (
    <MotionBtn
      id={id}
      onClick={props.onClick}
      type={props.htmlType ?? 'button'}
      isLoading={isLoading}
      disabled={isLoading || disabled}
      variant="solid"
      role="button"
      aria-label={typeof children === 'string' ? children : 'Button'}
      {...sharedBtnStyles({...props, spinAnimation})}
      {...motionProps}
    >
      {children}
    </MotionBtn>
  );
};

export const ButtonComponent: VFC<ButtonComponentDefinition> = (definition) => {
  const {props, id} = definition;
  const {animationStates} = props;
  const [activeVariant, setActiveVariant] = useState<string | undefined>(
    undefined
  );
  const motionOptions = useMotionOptions(animationStates);

  const styles = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;

  const {observable, broadcast} = useShowInstructions(instructions, definition);

  const onClick = (): void => {
    if ('onClick' in props && typeof props.onClick === 'function') {
      props.onClick();
    }

    broadcast({type: `${namespace}:on-click`, meta: {}});
  };

  useSubscription(observable, (inst) => {
    if (inst.type === `${namespace}:click`) {
      onClick();
    } else if (inst.type === 'Button:animationState') {
      applyTween(inst, animationStates, setActiveVariant);
    }
  });

  return (
    <Button
      {...props}
      onClick={onClick}
      cssProp={styles}
      id={id}
      motionProps={{
        animate: activeVariant,
        ...motionOptions,
      }}
    />
  );
};
